import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback } from 'react';

import Icon from '../../OC/oc-icon';

const DropZone = styled.div`
  border: 2px dashed ${props => (props.isDragActive ? '#d40f7d' : '#bcbcbc')};
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  background-color: ${props => (props.isDragActive ? '#fef7fb' : '#f8f8f8')};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border-color: #d40f7d;
    background-color: #fef7fb;
  }

  &.error {
    border-color: #940420;
    background-color: #fff5f5;
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  color: ${props => (props.isDragActive ? '#d40f7d' : '#6d6d6d')};
  margin-bottom: 16px;
  transition: color 0.3s ease;
`;

const UploadTitle = styled.h3`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0 0 8px 0;
`;

const UploadSubtitle = styled.p`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;
  color: #6d6d6d;
  margin-top: 10px;
`;

const FileList = styled.div`
  margin-top: 16px;
  width: 100%;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoText = styled.div`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 12px;
  color: #6d6d6d;
  text-align: center;
`;

const AdiitionalNote = styled.div`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;
  color: #6d6d6d;
  text-align: left;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const FileName = styled.span`
  margin-left: 8px;
  color: #333;
`;

const FileSize = styled.span`
  margin-left: 8px;
  color: #6d6d6d;
  font-size: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #940420;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;

  &:hover {
    color: #c0002a;
  }
`;

const ErrorMessage = styled.div`
  color: #940420;
  font-size: 12px;
  font-weight: bold;
  margin-top: 8px;
  text-align: left;
`;

const HiddenInput = styled.input`
  display: none;
`;

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

const validateFile = (file, accept, maxFileSize) => {
  const errors = [];

  if (accept) {
    const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const mimeType = file.type.toLowerCase();

    const isAccepted = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return type === fileExtension;
      }
      return mimeType.startsWith(type.replace('*', ''));
    });

    if (!isAccepted) {
      errors.push(`File type not accepted. Accepted formats: ${accept}`);
    }
  }

  if (maxFileSize && file.size > maxFileSize) {
    errors.push(`File size exceeds limit of ${formatFileSize(maxFileSize)}`);
  }

  return errors;
};

const convertFileToDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const addNameParamToDataUrl = (dataUrl, fileName) => {
  if (!dataUrl || !fileName) return dataUrl;
  const marker = ';base64,';
  const idx = dataUrl.indexOf(marker);
  if (idx === -1) return dataUrl;
  const header = dataUrl.substring(0, idx);
  if (header.includes(';name=')) return dataUrl;
  const encodedName = encodeURIComponent(fileName);
  return `${header};name=${encodedName}${marker}${dataUrl.substring(idx + marker.length)}`;
};

const DragAndDropFileWidget = props => {
  const { id, value = [], disabled, readonly, onChange, options = {}, schema = {} } = props;

  const { accept = '.pdf,.jpg,.jpeg,.png', maxFileSize = 10485760 } = options;

  const maxFiles = options.maxFiles || schema.maxItems || 5;
  const additionalNote = schema.additionalNote || '';

  const [isDragActive, setIsDragActive] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const handleFiles = useCallback(
    async files => {
      if (disabled || readonly) return;

      const fileArray = Array.from(files);
      const newErrors = [];
      const validFiles = [];

      if (value.length + fileArray.length > maxFiles) {
        newErrors.push(`Cannot upload more than ${maxFiles} files`);
        setErrors(newErrors);
        return;
      }

      fileArray.forEach(file => {
        const fileErrors = validateFile(file, accept, maxFileSize);
        if (fileErrors.length > 0) {
          newErrors.push(...fileErrors);
        } else {
          validFiles.push(file);
        }
      });

      if (newErrors.length > 0) {
        setErrors(newErrors);
        return;
      }

      try {
        const fileData = await Promise.all(
          validFiles.map(file => convertFileToDataUrl(file).then(d => addNameParamToDataUrl(d, file.name)))
        );

        const newValue = [...value, ...fileData]
          .map(item => (typeof item === 'string' ? item : item?.dataUrl))
          .filter(Boolean);
        onChange(newValue);
        setErrors([]);
      } catch (error) {
        setErrors(['Error reading files']);
      }
    },
    [value, onChange, disabled, readonly, accept, maxFileSize, maxFiles]
  );

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragActive(false);

      if (disabled || readonly) return;

      const { files } = e.dataTransfer;
      handleFiles(files);
    },
    [handleFiles, disabled, readonly]
  );

  const handleDragOver = useCallback(
    e => {
      e.preventDefault();
      if (!disabled && !readonly) {
        setIsDragActive(true);
      }
    },
    [disabled, readonly]
  );

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleFileInputChange = useCallback(
    e => {
      const { files } = e.target;
      if (files && files.length > 0) {
        handleFiles(files);
      }
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleBrowseClick = useCallback(() => {
    if (!disabled && !readonly) {
      fileInputRef.current?.click();
    }
  }, [disabled, readonly]);

  const handleRemoveFile = useCallback(
    index => {
      if (disabled || readonly) return;

      const newValue = value
        .filter((_, i) => i !== index)
        .map(item => (typeof item === 'string' ? item : item?.dataUrl))
        .filter(Boolean);
      onChange(newValue);
      setErrors([]);
    },
    [value, onChange, disabled, readonly]
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleBrowseClick();
      }
    },
    [handleBrowseClick]
  );

  const getFileDisplayInfo = (fileItem, index) => {
    if (typeof fileItem === 'object' && fileItem.dataUrl) {
      return {
        name: fileItem.name,
        size: fileItem.size,
        type: fileItem.type,
        dataUrl: fileItem.dataUrl,
      };
    }

    const dataUrl = fileItem;
    const defaultName = `file-${index + 1}`;
    const mimeMatch = dataUrl.match(/^data:([^;]+);/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

    const base64Data = dataUrl.split(',')[1];
    const sizeInBytes = base64Data ? (base64Data.length * 3) / 4 : 0;

    let extension = '';
    if (mimeType.includes('image/jpeg')) extension = '.jpg';
    else if (mimeType.includes('image/png')) extension = '.png';
    else if (mimeType.includes('image/gif')) extension = '.gif';
    else if (mimeType.includes('application/pdf')) extension = '.pdf';

    const nameParamMatch = dataUrl.match(/;name=([^;]+)/);
    const nameFromParam = nameParamMatch ? decodeURIComponent(nameParamMatch[1]) : null;

    return {
      name: nameFromParam || defaultName + extension,
      size: sizeInBytes,
      type: mimeType,
      dataUrl,
    };
  };

  const acceptedFormats = accept
    .split(',')
    .map(format => format.trim())
    .join(', ');

  return (
    <div>
      <DropZone
        id={id}
        isDragActive={isDragActive}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleBrowseClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled || readonly ? -1 : 0}
        role="button"
        aria-label="Upload files"
        className={errors.length > 0 ? 'error' : ''}
      >
        <UploadIcon>
          <Icon type="cloud-upload" />
        </UploadIcon>

        <UploadTitle>
          Drag and Drop your files here or{' '}
          <a
            href="#browse"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleBrowseClick();
            }}
          >
            Browse
          </a>
        </UploadTitle>
        <>
          <InfoText>Accepted file formats include {acceptedFormats}.</InfoText>
          <InfoText>Files must be {formatFileSize(maxFileSize)} or less.</InfoText>
        </>
      </DropZone>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        onChange={handleFileInputChange}
        disabled={disabled || readonly}
      />

      {additionalNote && (
        <AdiitionalNote>
          <UploadSubtitle>{additionalNote}</UploadSubtitle>
        </AdiitionalNote>
      )}

      {value && value.length > 0 && (
        <FileList>
          {value.map((fileItem, fileIndex) => {
            const fileInfo = getFileDisplayInfo(fileItem, fileIndex);
            return (
              <FileItem key={`file-${fileInfo.name}-${fileInfo.size}`}>
                <FileInfo>
                  <Icon type="file" />
                  <FileName>{fileInfo.name}</FileName>
                  <FileSize>({formatFileSize(fileInfo.size)})</FileSize>
                </FileInfo>
                {!disabled && !readonly && (
                  <RemoveButton
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFile(fileIndex);
                    }}
                    aria-label={`Remove ${fileInfo.name}`}
                  >
                    <Icon type="times" />
                  </RemoveButton>
                )}
              </FileItem>
            );
          })}
        </FileList>
      )}

      {errors.length > 0 && (
        <ErrorMessage>
          {errors.map(error => (
            <div key={`error-${error.substring(0, 10)}`}>{error}</div>
          ))}
        </ErrorMessage>
      )}
    </div>
  );
};

DragAndDropFileWidget.defaultProps = {
  value: [],
  disabled: false,
  readonly: false,
  options: {
    accept: '.pdf,.jpg,.jpeg,.png',
    maxFileSize: 10485760,
  },
  schema: {},
};

if (process.env.NODE_ENV !== 'production') {
  DragAndDropFileWidget.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          dataUrl: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number.isRequired,
          type: PropTypes.string.isRequired,
        }),
      ])
    ),
    disabled: PropTypes.bool,
    readonly: PropTypes.bool,

    onChange: PropTypes.func.isRequired,
    options: PropTypes.shape({
      accept: PropTypes.string,
      maxFileSize: PropTypes.number,
      maxFiles: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    schema: PropTypes.shape({
      title: PropTypes.string,
      additionalNote: PropTypes.string,
      maxItems: PropTypes.number,
    }),
  };
}

export default DragAndDropFileWidget;
